import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';

export default function HealthChartsSection({ healthData }) {
  if (!healthData || healthData.length === 0) return null;

  return (
    <>
      {/* Heart Rate Trends */}
      {healthData.some(d => d.heart_rate_avg) && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="heart-rate-chart">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Heart Rate Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={healthData.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis domain={[40, 180]} label={{ value: 'BPM', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                formatter={(value) => [`${value} bpm`, '']}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="heart_rate_max" 
                stroke="#EF4444" 
                fill="#FEE2E2" 
                name="Max HR"
                fillOpacity={0.3}
              />
              <Area 
                type="monotone" 
                dataKey="heart_rate_avg" 
                stroke="#F59E0B" 
                fill="#FEF3C7" 
                name="Avg HR"
                fillOpacity={0.5}
              />
              <Area 
                type="monotone" 
                dataKey="heart_rate_min" 
                stroke="#10B981" 
                fill="#D1FAE5" 
                name="Min HR"
                fillOpacity={0.3}
              />
              <Line 
                type="monotone" 
                dataKey="heart_rate_resting" 
                stroke="#6366F1" 
                strokeWidth={2}
                name="Resting HR"
                dot={{ fill: '#6366F1' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Activity & Sleep Dual Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Steps & Distance */}
        {healthData.some(d => d.steps) && (
          <div className="bg-white rounded-lg shadow-lg p-6" data-testid="activity-chart">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Daily Activity</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={healthData.slice().reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis yAxisId="left" label={{ value: 'Steps', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Distance (km)', angle: 90, position: 'insideRight' }} />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="steps" fill="#10B981" name="Steps" />
                <Line yAxisId="right" type="monotone" dataKey="distance" stroke="#3B82F6" strokeWidth={2} name="Distance (km)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Sleep Quality */}
        {healthData.some(d => d.sleep_hours) && (
          <div className="bg-white rounded-lg shadow-lg p-6" data-testid="sleep-chart">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Sleep Patterns</h2>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={healthData.slice().reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis yAxisId="left" domain={[0, 12]} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'Quality %', angle: 90, position: 'insideRight' }} />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Legend />
                <Area 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="sleep_hours" 
                  stroke="#8B5CF6" 
                  fill="#DDD6FE" 
                  name="Sleep Hours"
                  fillOpacity={0.6}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="sleep_quality_score" 
                  stroke="#F59E0B" 
                  strokeWidth={2}
                  name="Sleep Quality %"
                  dot={{ fill: '#F59E0B' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Energy Expenditure */}
      {healthData.some(d => d.active_calories) && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="energy-chart">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Energy Expenditure</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={healthData.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis yAxisId="left" label={{ value: 'Calories', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" label={{ value: 'Minutes', angle: 90, position: 'insideRight' }} />
              <Tooltip 
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="active_calories" fill="#EF4444" name="Active Calories" />
              <Bar yAxisId="left" dataKey="total_calories" fill="#F59E0B" name="Total Calories" />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="exercise_minutes" 
                stroke="#10B981" 
                strokeWidth={3}
                name="Exercise Minutes"
                dot={{ fill: '#10B981' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}
